import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ProfessionalLayout from '../components/ProfessionalLayout';
import api from '../lib/api';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Filter,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Eye,
  Package,
  X,
  Camera,
  ZoomIn,
  FileText,
  Plus,
  Truck,
  User,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  open: { label: 'Ouvert', color: 'text-red-600', bg: 'bg-red-50', icon: AlertTriangle },
  acknowledged: { label: 'Pris en charge', color: 'text-blue-600', bg: 'bg-blue-50', icon: Eye },
  resolved: { label: 'Résolu', color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle },
  closed: { label: 'Fermé', color: 'text-gray-500', bg: 'bg-gray-50', icon: CheckCircle },
};

const CATEGORY_LABELS = {
  damaged_package: 'Colis endommagé',
  recipient_absent: 'Destinataire absent',
  wrong_address: 'Mauvaise adresse',
  refused_by_recipient: 'Refusé par destinataire',
  missing_package: 'Colis manquant',
  customs_issue: 'Problème douane',
  other: 'Autre',
};

const PAGE_SIZE = 20;
const MAX_PHONE_DIGITS = 15;

/**
 * Format phone with auto-spacing: groups of 2 from right.
 * "612344556" → "6 12 34 45 56"
 * "0612345678" → "06 12 34 56 78"
 */
const formatPhone = (raw) => {
  const hasPlus = raw.startsWith('+');
  const digits = raw.replace(/\D/g, '').slice(0, MAX_PHONE_DIGITS);
  if (!digits) return hasPlus ? '+' : '';
  const parts = [];
  let i = digits.length;
  while (i > 0) {
    const start = Math.max(0, i - 2);
    parts.unshift(digits.slice(start, i));
    i = start;
  }
  return (hasPlus ? '+' : '') + parts.join(' ');
};

/** Strip spaces to get raw digits (+ optional leading +) */
const rawPhone = (formatted) => formatted.replace(/\s/g, '');

const IncidentsPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [shipmentFilter, setShipmentFilter] = useState('');
  const [contactFilter, setContactFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);
  const [photoViewer, setPhotoViewer] = useState(null);
  const [resolveTemplates, setResolveTemplates] = useState([]);

  // Create dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    package_id: '',
    shipment_id: '',
    contact_email: '',
    contact_phone: '',
    category: '',
    description: '',
  });
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [shipments, setShipments] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);        // jusqu'à 3 photos
  const [notifyParties, setNotifyParties] = useState(true); // notifier les concernés

  const isAdmin = ['super_admin', 'company_admin', 'country_admin'].includes(user?.role);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/api/broadcast/templates');
      setResolveTemplates(res.data.templates || []);
    } catch {}
  }, []);

  const fetchShipments = useCallback(async () => {
    try {
      const res = await api.get(`${API}/api/shipments`);
      setShipments(res.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchIncidents();
      fetchTemplates();
      fetchShipments();
    }
  }, [statusFilter, categoryFilter, shipmentFilter, contactFilter, page]);

  const fetchIncidents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status_filter', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      if (shipmentFilter) params.append('shipment_id', shipmentFilter);
      if (contactFilter) params.append('contact_filter', contactFilter);
      params.append('page', page);
      params.append('limit', PAGE_SIZE);
      const res = await api.get(`${API}/api/incidents?${params.toString()}`);
      setIncidents(res.data.data || []);
      setPagination(res.data.pagination || null);
    } catch (err) {
      console.error('Error fetching incidents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (incidentId) => {
    if (!resolveNote.trim()) return;
    setResolving(true);
    try {
      await api.put(`${API}/api/incidents/${incidentId}/resolve`, {
        resolution_note: resolveNote,
        new_status: 'resolved',
      });
      setSelectedIncident(null);
      setResolveNote('');
      fetchIncidents();
    } catch (err) {
      console.error('Error resolving incident:', err);
    } finally {
      setResolving(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');

    const phoneClean = rawPhone(createForm.contact_phone);
    const hasAssociation = createForm.package_id || createForm.shipment_id || createForm.contact_email || phoneClean;
    if (!hasAssociation) {
      setCreateError('Au moins une association est requise (colis, départ ou contact)');
      return;
    }
    if (!createForm.category || !createForm.description.trim()) {
      setCreateError('Catégorie et description sont obligatoires');
      return;
    }

    setCreateSubmitting(true);
    try {
      const payload = {
        category: createForm.category,
        description: createForm.description.slice(0, 1000),
      };
      if (createForm.package_id) payload.package_id = createForm.package_id;
      if (createForm.shipment_id) payload.shipment_id = createForm.shipment_id;
      if (createForm.contact_email) payload.contact_email = createForm.contact_email;
      if (phoneClean) payload.contact_phone = phoneClean;

      const res = await api.post(`${API}/api/incidents`, payload);
      const incidentId = res.data?.id;

      // Upload des photos (jusqu'à 3), séquentiel
      for (const file of photoFiles.slice(0, 3)) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`${API}/api/incidents/${incidentId}/photo`, fd);
      }

      // Notifier les parties concernées (in-app + WhatsApp)
      if (notifyParties && incidentId) {
        try {
          await api.post(`${API}/api/incidents/${incidentId}/notify`);
        } catch (notifyErr) {
          console.error('Notify failed:', notifyErr);
        }
      }

      setShowCreateDialog(false);
      setCreateForm({ package_id: '', shipment_id: '', contact_email: '', contact_phone: '', category: '', description: '' });
      setPhotoFiles([]);
      setNotifyParties(true);
      fetchIncidents();
    } catch (err) {
      setCreateError(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const renderTargetBadges = (inc) => {
    const badges = [];
    if (inc.package_oce_code || inc.package_id) {
      badges.push(
        <span key="pkg" className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
          <Package size={10} />
          {inc.package_oce_code || inc.package_id?.slice(0, 8)}
        </span>
      );
    }
    if (inc.shipment_name || inc.shipment_id) {
      badges.push(
        <span key="ship" className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">
          <Truck size={10} />
          {inc.shipment_name || 'Départ'}
        </span>
      );
    }
    if (inc.contact_name || inc.contact_email || inc.contact_phone) {
      badges.push(
        <span key="contact" className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
          <User size={10} />
          {inc.contact_name || inc.contact_email || inc.contact_phone}
        </span>
      );
    }
    return badges.length > 0 ? <div className="flex flex-wrap gap-1">{badges}</div> : <span className="text-muted-foreground">-</span>;
  };

  if (!isAdmin) {
    return (
      <ProfessionalLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-muted-foreground">{t('accessDenied') || 'Access denied'}</p>
        </div>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('incidents')}</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {t('reportedIncidents')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCreateDialog(true)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
            >
              <Plus size={14} />
              Signaler un incident
            </button>
            <button
              onClick={() => { setPage(1); fetchIncidents(); }}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-md hover:bg-muted transition-colors"
            >
              <RefreshCw size={14} />
              {t('refresh') || 'Actualiser'}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="text-sm border border-border rounded-md px-2 py-1.5 bg-background"
            >
              <option value="">Tous les statuts</option>
              <option value="open">Ouvert</option>
              <option value="acknowledged">Pris en charge</option>
              <option value="resolved">Résolu</option>
              <option value="closed">Fermé</option>
            </select>
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
            className="text-sm border border-border rounded-md px-2 py-1.5 bg-background"
          >
            <option value="">Toutes les catégories</option>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select
            value={shipmentFilter}
            onChange={(e) => { setShipmentFilter(e.target.value); setPage(1); }}
            className="text-sm border border-border rounded-md px-2 py-1.5 bg-background"
          >
            <option value="">Tous les départs</option>
            {shipments.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Filtrer par client (email/tel/nom)"
            value={contactFilter}
            onChange={(e) => { setContactFilter(e.target.value); setPage(1); }}
            className="text-sm border border-border rounded-md px-2 py-1.5 bg-background w-56"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : incidents.length === 0 ? (
          <div className="text-center py-16">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{t('noIncidents')}</p>
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Cible</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Catégorie</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Statut</th>
                    <th className="text-center px-4 py-3 font-medium text-muted-foreground"><Camera size={14} className="inline" /></th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Signalé par</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {incidents.map((inc) => {
                    const statusCfg = STATUS_CONFIG[inc.status] || STATUS_CONFIG.open;
                    const StatusIcon = statusCfg.icon;
                    return (
                      <tr key={inc.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          {renderTargetBadges(inc)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
                            {CATEGORY_LABELS[inc.category] || inc.category}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${statusCfg.bg} ${statusCfg.color}`}>
                            <StatusIcon size={12} />
                            {statusCfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {inc.photo_url ? (
                            <Camera size={14} className="inline text-blue-500" title="Photo disponible" />
                          ) : (
                            <span className="text-muted-foreground/40">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {inc.reporter_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {formatDate(inc.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedIncident(inc)}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Détails
                            </button>
                            {inc.status === 'open' && (
                              <button
                                onClick={() => { setSelectedIncident(inc); setResolveNote(''); }}
                                className="text-xs text-green-600 hover:underline"
                              >
                                {t('resolveIncident')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Page {pagination.page} / {pagination.total_pages} ({pagination.total} incidents)
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={!pagination.has_prev}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="p-1.5 border border-border rounded-md disabled:opacity-40 hover:bg-muted transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                disabled={!pagination.has_next}
                onClick={() => setPage(p => p + 1)}
                className="p-1.5 border border-border rounded-md disabled:opacity-40 hover:bg-muted transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail / Resolve Modal */}
      {selectedIncident && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-border rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold flex items-center gap-2">
                <AlertTriangle size={16} className="text-orange-500" />
                Détail incident
              </h3>
              <button onClick={() => setSelectedIncident(null)} className="text-muted-foreground hover:text-foreground">
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Associations */}
              <div className="text-sm space-y-2">
                <span className="text-muted-foreground">Associations</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {renderTargetBadges(selectedIncident)}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Catégorie</span>
                  <p className="font-medium">{CATEGORY_LABELS[selectedIncident.category] || selectedIncident.category}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Statut</span>
                  <p className="font-medium">{STATUS_CONFIG[selectedIncident.status]?.label || selectedIncident.status}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Signalé par</span>
                  <p className="font-medium">{selectedIncident.reporter_name || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Date</span>
                  <p className="font-medium">{formatDate(selectedIncident.created_at)}</p>
                </div>
              </div>

              <div className="text-sm">
                <span className="text-muted-foreground">Description</span>
                <p className="mt-1 p-3 bg-muted rounded-md">{selectedIncident.description}</p>
              </div>

              {selectedIncident.photo_url && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Photo</span>
                  <div
                    className="mt-1 relative group cursor-pointer inline-block"
                    onClick={() => setPhotoViewer(`${API}${selectedIncident.photo_url}`)}
                  >
                    <img
                      src={`${API}${selectedIncident.photo_url}`}
                      alt="Incident"
                      className="rounded-md max-h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors rounded-md flex items-center justify-center">
                      <ZoomIn size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </div>
              )}

              {selectedIncident.resolution_note && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Note de résolution</span>
                  <p className="mt-1 p-3 bg-green-50 rounded-md text-green-800">{selectedIncident.resolution_note}</p>
                </div>
              )}

              {/* Resolve form — only for open incidents */}
              {selectedIncident.status === 'open' && (
                <div className="border-t border-border pt-4">
                  <label className="block text-sm font-medium mb-2">{t('resolveIncident')}</label>

                  {/* Template selector */}
                  {resolveTemplates.length > 0 && (
                    <div className="flex items-center gap-2 mb-3">
                      <FileText size={14} className="text-muted-foreground flex-shrink-0" />
                      <select
                        onChange={(e) => {
                          const tpl = resolveTemplates.find(t => t.id === e.target.value);
                          if (tpl) setResolveNote(tpl.message);
                        }}
                        defaultValue=""
                        className="flex-1 border border-border rounded-md px-2 py-1.5 text-sm bg-background"
                      >
                        <option value="">-- Utiliser un modèle --</option>
                        {resolveTemplates
                          .filter(t => t.theme === 'incident_package')
                          .length > 0 && (
                          <optgroup label="Incidents">
                            {resolveTemplates
                              .filter(t => t.theme === 'incident_package')
                              .map(tpl => (
                                <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                              ))}
                          </optgroup>
                        )}
                        <optgroup label="Autres">
                          {resolveTemplates
                            .filter(t => t.theme !== 'incident_package')
                            .map(tpl => (
                              <option key={tpl.id} value={tpl.id}>{tpl.name} — {tpl.subject}</option>
                            ))}
                        </optgroup>
                      </select>
                    </div>
                  )}

                  <textarea
                    value={resolveNote}
                    onChange={(e) => setResolveNote(e.target.value)}
                    placeholder="Note de résolution..."
                    rows={3}
                    maxLength={1000}
                    className="w-full p-2 border border-border rounded-md text-sm bg-background"
                  />
                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      onClick={() => setSelectedIncident(null)}
                      className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={() => handleResolve(selectedIncident.id)}
                      disabled={resolving || !resolveNote.trim()}
                      className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-1"
                    >
                      {resolving && <RefreshCw size={12} className="animate-spin" />}
                      <CheckCircle size={14} />
                      Résoudre
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Incident Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-border rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold flex items-center gap-2">
                <AlertTriangle size={16} className="text-orange-500" />
                Signaler un incident
              </h3>
              <button onClick={() => setShowCreateDialog(false)} className="text-muted-foreground hover:text-foreground">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreate} className="p-4 space-y-4">
              <p className="text-xs text-muted-foreground">Au moins une association est requise (colis, départ ou contact client).</p>

              {createError && (
                <div className="text-sm text-red-600 bg-red-50 p-2 rounded-md">{createError}</div>
              )}

              {/* 1. Shipment (départ = origine → destination) */}
              <div>
                <label className="block text-sm font-medium mb-1">Départ (origine → destination)</label>
                <select
                  value={createForm.shipment_id}
                  onChange={(e) => setCreateForm(f => ({ ...f, shipment_id: e.target.value }))}
                  className="w-full p-2 border border-border rounded-md text-sm bg-background"
                >
                  <option value="">-- Sélectionner un départ (optionnel) --</option>
                  {shipments.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.origin_country && s.destination_country ? ` (${s.origin_country} → ${s.destination_country})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* 2. Contact client */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Email client</label>
                  <input
                    type="email"
                    value={createForm.contact_email}
                    onChange={(e) => setCreateForm(f => ({ ...f, contact_email: e.target.value }))}
                    className="w-full p-2 border border-border rounded-md text-sm bg-background"
                    placeholder="email@exemple.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tél client</label>
                  <input
                    type="tel"
                    value={createForm.contact_phone}
                    onChange={(e) => setCreateForm(f => ({ ...f, contact_phone: formatPhone(e.target.value) }))}
                    className="w-full p-2 border border-border rounded-md text-sm bg-background font-mono tracking-wide"
                    placeholder="6 12 34 45 56"
                    maxLength={22}
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium mb-1">Catégorie *</label>
                <select
                  required
                  value={createForm.category}
                  onChange={(e) => setCreateForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full p-2 border border-border rounded-md text-sm bg-background"
                >
                  <option value="">-- Sélectionner --</option>
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-1">Description *</label>
                <textarea
                  required
                  value={createForm.description}
                  onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full p-2 border border-border rounded-md text-sm bg-background"
                  rows={3}
                  maxLength={1000}
                  placeholder="Décrivez le problème..."
                />
              </div>

              {/* Photos (jusqu'à 3) */}
              <div>
                <label className="block text-sm font-medium mb-1">Photos (jusqu'à 3)</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={(e) => setPhotoFiles(Array.from(e.target.files || []).slice(0, 3))}
                  className="w-full text-sm"
                />
                {photoFiles.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {photoFiles.length} photo(s) sélectionnée(s){photoFiles.length > 3 ? ' (max 3)' : ''}
                  </p>
                )}
              </div>

              {/* Notifier les parties concernées */}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={notifyParties}
                  onChange={(e) => setNotifyParties(e.target.checked)}
                  className="rounded"
                />
                Notifier les parties concernées (partenaires, admins, client) par WhatsApp + in-app
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateDialog(false)}
                  className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={createSubmitting}
                  className="px-3 py-1.5 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-1"
                >
                  {createSubmitting && <RefreshCw size={12} className="animate-spin" />}
                  <AlertTriangle size={14} />
                  Signaler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Full-size photo viewer */}
      {photoViewer && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4 cursor-pointer"
          onClick={() => setPhotoViewer(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            onClick={() => setPhotoViewer(null)}
          >
            <X size={28} />
          </button>
          <img
            src={photoViewer}
            alt="Incident"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </ProfessionalLayout>
  );
};

export default IncidentsPage;
